import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { firstValueFrom } from 'rxjs';

import { DcCoreElementType } from '../../core/dc-core-types';
import { ElementPermission } from '../../core/domain/permission-types';
import { DcBaseRequester } from '../core/dc-base.requester';

@Injectable({
	providedIn: 'root',
})
export class AjsPermissionRequester extends DcBaseRequester<unknown> {
	protected resource: string;
	protected elementsListUrl: string;
	protected singleElementUrl: string;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = '';
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public async haveCreatePermissionForElement(
		el: DcCoreElementType
	): Promise<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_USER_GLOBAL_PERMISSION'),
			{
				action: `${el.toLowerCase()}-${ElementPermission.Create}`,
			},
			{}
		);
		return await firstValueFrom(
			this.get<boolean>(
				url,
				CachePolicyEntity.build({
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
				})
			)
		);
	}

	public async haveListPermissionForElement(
		el: DcCoreElementType
	): Promise<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_USER_GLOBAL_PERMISSION'),
			{
				action: `${el.toLowerCase()}`,
			},
			{}
		);
		return await firstValueFrom(
			this.get<boolean>(
				url,
				CachePolicyEntity.build({
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
				})
			)
		);
	}

	public async haveViewPermissionFor(
		el: DcCoreElementType,
		elId: number
	): Promise<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_PERMISSION'),
			{
				elementId: elId,
				type: `${el.toLowerCase()}`,
				action: 'view',
			},
			{
				project: this.projectId,
			}
		);

		return await firstValueFrom(
			this.get<boolean>(
				url,
				CachePolicyEntity.build({
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
				})
			)
		);
	}

	public async haveElementPermission(
		el: DcCoreElementType,
		elId: number,
		permission: ElementPermission
	): Promise<boolean> {
		const url = this.urlService.generateUrl(
			this.generateResourceUrl('PERMISSIONS_CHECK_PERMISSION'),
			{
				elementId: elId,
				type: `${el.toLowerCase()}`,
				action: permission,
			},
			{
				project: this.projectId,
			}
		);

		return await firstValueFrom(
			this.get<boolean>(
				url,
				CachePolicyEntity.build({
					isCacheable: false,
					policy: CachePolicy.NetworkOnly,
					maxAgeInSeconds: 0,
				})
			)
		);
	}
}
